import { DEFAULT_FORM_ERROR } from '../../constants/form';
import type { GraphQLExtensions } from '../../types/graphql';
import { getSignupErrorTranslationKey } from '../signup';
import { getEmailErrorMessage } from './email';
import { isErrorWithNonEmptyExtensions } from './error-with-extensions';
import { hasCodeExtension, isEmailCodeExtension, isExtensionWithMessage } from './gql-extensions';

interface GetDefaultErrorWithMessageParams {
  hasGqlErrorExtensions: boolean;
  defaultError?: string;
}

const getEmailErrorWithMessage = (errorCode: string) => ({
  errorMessage: getEmailErrorMessage(errorCode),
  hasGqlErrorExtensions: true,
});

const getExtensionErrorWithMessage = (message: string) => ({
  errorMessage: {
    message,
    translated: true,
    withOptions: false,
    timestamp: Date.now(),
  },
  hasGqlErrorExtensions: true,
});

const getExtensionErrorWithCode = (code: string, fallbackError: string) => ({
  errorMessage: {
    message: getSignupErrorTranslationKey(code, fallbackError),
    translated: false,
    withOptions: false,
    timestamp: Date.now(),
  },
  hasGqlErrorExtensions: true,
});

const getDefaultErrorWithMessage = ({
  hasGqlErrorExtensions,
  defaultError,
}: GetDefaultErrorWithMessageParams) => ({
  errorMessage: {
    message: defaultError || DEFAULT_FORM_ERROR,
    translated: false,
    withOptions: false,
    timestamp: Date.now(),
  },
  hasGqlErrorExtensions,
});

const getRegisterErrorMessageFromExtensions = (
  extensions: GraphQLExtensions,
  defaultError?: string
) => {
  if (isEmailCodeExtension(extensions)) {
    return getEmailErrorWithMessage(extensions.email.code);
  }

  const [firstError] = Object.values(extensions);
  if (isExtensionWithMessage(firstError)) {
    return getExtensionErrorWithMessage(firstError.message);
  }

  if (hasCodeExtension(extensions)) {
    return getExtensionErrorWithCode(extensions.code, defaultError);
  }

  return getDefaultErrorWithMessage({
    hasGqlErrorExtensions: true,
    defaultError,
  });
};

export const getRegisterErrorMessage = (error: unknown, defaultError?: string) => {
  if (isErrorWithNonEmptyExtensions(error)) {
    return getRegisterErrorMessageFromExtensions(error.extensions, defaultError);
  }

  return getDefaultErrorWithMessage({
    hasGqlErrorExtensions: false,
    defaultError,
  });
};
